import React, { useState } from "react";
import { Alert, Box, Button, Link, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { mfa } from "../../utils/securityService";

/**
 * Two-step sign-in setup (Nov 2026). Default: an authenticator app; text message as the fallback
 * when the district has it available. Used mid-sign-in (setupId) and from My Account (signedIn).
 */
const groupKey = (k) => (k || "").replace(/(.{4})/g, "$1 ").trim();

const MfaSetup = ({ setupId, signedIn, smsAvailable, onDone }) => {
  const [method, setMethod] = useState("app");
  const [setup, setSetup] = useState(null); // {secret, otpauthUri} | {destination}
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const base = setupId ? { setupId } : {};
  const opts = { signedIn: !!signedIn && !setupId };

  const begin = async () => {
    setBusy(true); setError("");
    const res = await mfa({ ...base, action: "start", method, phone }, opts);
    setBusy(false);
    if (res.ok) setSetup(res.data); else setError(res.message);
  };

  const confirm = async () => {
    setBusy(true); setError("");
    const res = await mfa({ ...base, action: "confirm", method, code }, opts);
    setBusy(false);
    if (res.ok) onDone && onDone(res.data); else setError(res.message);
  };

  const switchMethod = (_, v) => { if (v) { setMethod(v); setSetup(null); setCode(""); setError(""); } };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 520 }}>
      {smsAvailable && (
        <ToggleButtonGroup exclusive size="small" value={method} onChange={switchMethod}>
          <ToggleButton value="app" sx={{ textTransform: "none" }}>Authenticator app (recommended)</ToggleButton>
          <ToggleButton value="sms" sx={{ textTransform: "none" }}>Text message</ToggleButton>
        </ToggleButtonGroup>
      )}

      {method === "app" && !setup && (
        <>
          <Typography sx={{ fontSize: 14 }}>
            You'll need a free authenticator app on your phone, such as <strong>Google Authenticator</strong> or{" "}
            <strong>Microsoft Authenticator</strong>. Install one, then continue.
          </Typography>
          <Button variant="contained" disabled={busy} onClick={begin} sx={{ textTransform: "none", alignSelf: "flex-start" }}>
            {busy ? "Getting your key…" : "I have the app — continue"}
          </Button>
        </>
      )}
      {method === "app" && setup?.secret && (
        <>
          <Typography sx={{ fontSize: 14 }}>
            1. In the authenticator app, add an account and choose <strong>"Enter a setup key"</strong> (or "Enter code manually").
          </Typography>
          <Box sx={{ pl: 2, fontSize: 14 }}>
            <div>Account name: <strong>DiscloSure</strong></div>
            <div>Key: <strong style={{ fontFamily: "monospace", letterSpacing: 1 }}>{groupKey(setup.secret)}</strong></div>
            <div>Type: <strong>Time based</strong></div>
            <Link href={setup.otpauthUri} sx={{ fontSize: 13 }}>On this phone? Open in your authenticator app</Link>
          </Box>
          <Typography sx={{ fontSize: 14 }}>2. Enter the 6-digit code the app shows for DiscloSure:</Typography>
        </>
      )}

      {method === "sms" && !setup && (
        <>
          <Typography sx={{ fontSize: 14 }}>We'll text a code to this mobile number now, and each time you sign in.</Typography>
          <TextField size="small" label="Mobile number" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(410) 555-0123" />
          <Button variant="contained" disabled={busy || !phone} onClick={begin} sx={{ textTransform: "none", alignSelf: "flex-start" }}>
            {busy ? "Sending…" : "Text me a code"}
          </Button>
        </>
      )}
      {method === "sms" && setup?.destination && (
        <Typography sx={{ fontSize: 14 }}>Enter the 6-digit code we texted to {setup.destination}:</Typography>
      )}

      {setup && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField size="small" label="6-digit code" value={code} inputProps={{ inputMode: "numeric", maxLength: 7 }}
                     onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && confirm()} />
          <Button variant="contained" disabled={busy || code.replace(/\D/g, "").length !== 6} onClick={confirm} sx={{ textTransform: "none" }}>
            {busy ? "Checking…" : "Turn on"}
          </Button>
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}
    </Box>
  );
};

export default MfaSetup;
